import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';

import type { CalendarAvailabilityData } from '~/modules-rocco/booking/api/get-calendar-availability.server';
import type { BookingRosterMember } from '~/modules-rocco/booking/api/get-request-availability.server';
import { getRequestAvailability } from '~/modules-rocco/booking/api/get-request-availability.server';

export const BOOKING_REQUEST_AVAILABILITY_API_ROUTE =
    '/remix-api/booking/request-availability';

interface RequestAvailabilityError {
    code: 'notFound' | 'loadFailed' | 'enquiriesClosed';
    message: string;
}

export interface BookingRequestAvailabilityApiResponse {
    availability?: CalendarAvailabilityData;
    /** Everyone the practice shows, and how a meeting with each is made. */
    roster?: BookingRosterMember[];
    /** False when only calendar-backed members are offerable. */
    allowsEnquiries?: boolean;
    error?: RequestAvailabilityError;
}

const QUERY_SCHEMA = z.object({
    professional: z.string().min(1),
});

/**
 * Working-week times for a professional with no connected calendar, in the
 * shape the booking card already renders.
 *
 * Deliberately separate from the session availability route: there is no
 * session to disable, no calendar connection to check, and nothing to fetch
 * from a calendar provider. Submitting one of these goes to
 * {@link BOOKING_REQUEST_API_ROUTE}, not the booking mutation. See BC-5174.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const parsed = QUERY_SCHEMA.safeParse({
        professional: url.searchParams.get('professional'),
    });

    if (!parsed.success) {
        return routerData<BookingRequestAvailabilityApiResponse>(
            {
                error: {
                    code: 'notFound',
                    message: 'A professional is required.',
                },
            },
            { status: 400 },
        );
    }

    const result = await getRequestAvailability(
        parsed.data.professional,
        context,
    );

    if (!result.success) {
        return routerData<BookingRequestAvailabilityApiResponse>(
            { error: result.error },
            { status: result.error.code === 'notFound' ? 404 : 400 },
        );
    }

    return routerData<BookingRequestAvailabilityApiResponse>({
        availability: result.data,
        roster: result.roster,
        allowsEnquiries: result.allowsEnquiries,
    });
};
